import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from 'modules/auth';
import type { Permission } from 'modules/auth/permissions';
import { LoadingScreen } from 'components/ui/LoadingScreen';

interface RequirePermissionProps {
  /** A single key or a list (OR semantics — holding any one passes). */
  permission: Permission | Permission[];
  children: React.ReactNode;
  /** 'route': loading screen, then redirect when denied. 'affordance': nothing while loading or denied. */
  mode?: 'route' | 'affordance';
  /** Redirect target in route mode when access is denied. */
  redirectTo?: string;
}

/** SOFT gate — the API enforces. Never pair with a `state.role === …` check; fix the role→permission map. */
export function RequirePermission({
  permission,
  children,
  mode = 'route',
  redirectTo = '/app',
}: RequirePermissionProps) {
  const {
    state: { isFetched, accessFetched, permissions },
  } = useAuthContext();

  // Wait for BOTH flags. Permissions load fire-and-forget AFTER `isFetched`
  // flips, so gating on `isFetched` alone reads `permissions` empty on a hard
  // refresh and wrongly denies access — bouncing every gated page to `/app`,
  // which then lands the user on analytics. `accessFetched` flips true once
  // permissions are settled (even on request failure, so it never hangs). This
  // mirrors AppDefaultRedirect's guard.
  if (!isFetched || !accessFetched) {
    // Route mode renders inside the shell's page card, so the loader fills that
    // card rather than the viewport.
    return mode === 'route' ? <LoadingScreen fullScreen={false} /> : null;
  }

  const keys = Array.isArray(permission) ? permission : [permission];
  const hasAccess = keys.some((key) => permissions.includes(key));

  if (!hasAccess) {
    // Route mode bounces to the app index, which routes each role to a page it
    // can see (avoids loops now that /app/tests is itself permission-gated).
    return mode === 'route' ? <Navigate to={redirectTo} replace /> : null;
  }

  return <>{children}</>;
}
